import React, { useState, createContext, useContext, ReactNode, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faEdit, faCirclePlus } from '@fortawesome/free-solid-svg-icons';
import Image from '../components/Image';
import EntryDialog from './EntryDialog';
import ExportButton from './CPRLogPDF';
import { loadCurrentState, saveCurrentState } from './CprState/storage';
import { cprEventEmitter, EVENTS } from './cprEvents';
import './CPRLog.css';
import { useTranslation } from 'react-i18next';

export interface LogEntry {
  id: string;
  timestamp: string;
  text: string;
  type: 'openingConditions' | 'medication' | 'action' | 'measurement';
  isImportant: boolean;
}

export interface CPRLog {
  patientId: string;
  entries: LogEntry[];
}

interface CPRLogContextType {
  log: CPRLog;
  addEntry: (entry: Omit<LogEntry, 'id'>) => void;
  updateEntry: (id: string, updatedEntry: Partial<LogEntry>) => void;
  deleteEntry: (id: string) => void;
  clearAllEntries: () => void; 
}

const CPRLogContext = createContext<CPRLogContextType | undefined>(undefined);

// Helper function to generate a unique ID
const generateUniqueId = () => {
  return Date.now().toString() + '-' + Math.random().toString(36).substring(2, 9);
};

export const CPRLogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [log, setLog] = useState<CPRLog>(() => {
    // Initialize from storage on mount
    const savedState = loadCurrentState();
    return savedState?.log || { patientId: '', entries: [] };
  });

  useEffect(() => {
    const unsubscribe = cprEventEmitter.subscribe(EVENTS.RESET_CPR, () => {
      clearAllEntries();
    });
    return () => unsubscribe();
  }, []);

  const addEntry = (entry: Omit<LogEntry, 'id'>) => {
    const newEntry = { ...entry, id: generateUniqueId() };
    setLog(prevLog => {
      const newLog = {
        ...prevLog,
        entries: [...prevLog.entries, newEntry]
      };
      
      saveCurrentState({
        log: newLog
      }, 'log');
      
      return newLog;
    });
  };

  const updateEntry = (id: string, updatedEntry: Partial<LogEntry>) => {
    const newLog = {
      ...log,
      entries: log.entries.map(entry =>
        entry.id === id ? { ...entry, ...updatedEntry } : entry
      )
    };
    
    setLog(newLog);
    
    saveCurrentState({
      log: newLog
    }, 'log');
  };

  const clearAllEntries = () => {
    const newLog = {
      ...log,
      entries: []
    };
    
    setLog(newLog);
    
    saveCurrentState({
      log: newLog
    }, 'log');
  };

  const deleteEntry = (id: string) => {
    const newLog = {
      ...log,
      entries: log.entries.filter(entry => entry.id !== id)
    };
    
    setLog(newLog);
    
    saveCurrentState({
      log: newLog
    }, 'log');
  };

  return (
    <CPRLogContext.Provider value={{ log, addEntry, updateEntry, deleteEntry, clearAllEntries }}>
      {children}
    </CPRLogContext.Provider>
  );
};

export const useCPRLog = () => {
  const context = useContext(CPRLogContext);
  if (context === undefined) {
    throw new Error('useCPRLog must be used within a CPRLogProvider');
  }
  return context;
};

const CPRLogComponent: React.FC = () => {
  const { t } = useTranslation(['resus']);
  const { log, addEntry, updateEntry, deleteEntry } = useCPRLog();
  const [dialogEntry, setDialogEntry] = useState<(LogEntry & { type: LogEntry['type'] }) | null>(null);
  const { hospital } = useParams<{
    hospital: string;
  }>();

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const renderTable = (type: LogEntry['type'], title: string) => {
    const entries = log.entries.filter(entry => entry.type === type);

    return (
      <table className="cpr-log-table">
        <thead>
          <tr>
            <th colSpan={2}>
              <div className="table-header">
                <span>{title}</span>
                <button onClick={() => setDialogEntry({ id: '', timestamp: '', text: '', type, isImportant: false })}>
                  <FontAwesomeIcon icon={faCirclePlus} />
                </button>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry, index) => (
            <tr key={entry.id} className={index % 2 === 0 ? 'even' : 'odd'}>
              <td className="timestamp">{formatTime(entry.timestamp)}</td>
              <td>
                <div className="entry-content">
                  <span>
                    {entry.isImportant && <Image src="/assets/bullets/star.svg" alt="Important" className="important-icon" />}
                    {entry.text}
                  </span>
                  <div className="entry-actions">
                    <button onClick={() => setDialogEntry({...entry, type})}>
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button onClick={() => deleteEntry(entry.id)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="cpr-log-component">
      {renderTable('openingConditions', t('cprLog.openingConditionsTitle'))}
      {renderTable('medication', t('cprLog.medicationsGivenTitle'))}
      {renderTable('action', t('cprLog.actionsDoneTitle'))}
      {renderTable('measurement', t('cprLog.measurementsTitle'))}

      <ExportButton entries={log.entries} hospital={hospital ? hospital: "apps"} />

      {dialogEntry && (
        <EntryDialog
          entry={dialogEntry.id ? dialogEntry : undefined}
          type={dialogEntry.type}
          onSave={(entry) => {
            if (dialogEntry.id) {
              updateEntry(dialogEntry.id, {...entry, type: dialogEntry.type});
            } else {
              addEntry({...entry, type: dialogEntry.type});
            }
            setDialogEntry(null);
          }}
          onClose={() => setDialogEntry(null)}
        />
      )}
    </div>
  );
};

export default CPRLogComponent;
